import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { ToggleGroup } from '@base-ui-components/react/toggle-group'
import { Toggle } from '@base-ui-components/react/toggle'

export interface SegmentedOption {
  value: string
  label: ReactNode
}

export interface SegmentedProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
  value: string
  onChange: (value: string) => void
  options: SegmentedOption[]
}

/**
 * Two to four mutually exclusive choices, shown at once. Base UI's ToggleGroup
 * supplies the keyboard behaviour (arrow keys move, Home and End jump, focus
 * loops) and the roving tabindex; the look is `ui.css`'s.
 *
 * ToggleGroup speaks in arrays even when `multiple` is false, and pressing the
 * already-pressed item hands back an empty one. A segmented control with
 * nothing selected is not a state this has — it would leave the caller's
 * `value` pointing at an option no longer drawn as chosen — so an empty change
 * is dropped rather than passed on.
 */
export const Segmented = forwardRef<HTMLDivElement, SegmentedProps>(function Segmented(
  { value, onChange, options, className = '', ...rest },
  ref,
) {
  return (
    <ToggleGroup
      ref={ref}
      className={`u-seg ${className}`.trim()}
      value={[value]}
      onValueChange={next => { const v = next[0]; if (typeof v === 'string') onChange(v) }}
      {...rest}
    >
      {options.map(o => (
        <Toggle key={o.value} value={o.value} className="u-seg__item">
          {o.label}
        </Toggle>
      ))}
    </ToggleGroup>
  )
})
